import React from 'react';
import { Multiselect } from 'ui/Multiselect';
import { DecimalInput } from 'ui/stateful/DecimalInput';

interface IPricingInfoSectionProps {
  currency: string;
  fullPrice: number | null;
  discountedPrice: number | null;
  discountPercent: number | null;
  updateCurrency: (currency: string) => void;
  updateFullPrice: (fullPrice: number | null) => void;
  updateDiscountedPrice: (discountedPrice: number | null) => void;
  updateDiscountPercent: (discountPercent: number | null) => void;
}

const currencyOptions = [
  { value: 'EUR', label: 'EUR Euro' },
  { value: 'USD', label: 'USD Dollar' },
  { value: 'GBP', label: 'GBP Pound' },
  { value: 'AED', label: 'AED Dirham' },
];

export const PricingInfoSection = ({
  currency,
  fullPrice,
  discountedPrice,
  discountPercent,
  updateCurrency,
  updateFullPrice,
  updateDiscountedPrice,
  updateDiscountPercent,
}: IPricingInfoSectionProps) => {
  return (
    <div className="space-y-[10px]">
      {/* Section Title */}
      <span className="font-bold text-[17px] text-gray-800 font-pt-sans">Pricing Info</span>

      {/* Row with all fields */}
      <div className="w-fit grid grid-cols-1 gap-2 md:grid-cols-4 md:gap-[40px]">
        {/* Currency */}
        <div className="w-full md:w-[160px]">
          <label className="block text-[13px] font-pt-sans text-black mb-[5px] tracking-wider">Currency</label>
          <Multiselect
            options={currencyOptions}
            selectedValues={currency ? [currency] : []}
            onUpdate={values => updateCurrency(values[0] || '')}
            className="w-full"
            itemsClassname="bg-white-hard"
            itemCtaClassName="hover:bg-gray-10"
            placeholderText="EUR Euro"
            isSingleSelectMode={true}
            isCloseOnSelect={true}
            hideCheckboxes={true}
            primaryCtaClassName="bg-white-hard text-black"
          />
        </div>

        {/* Full Price */}
        <div className="w-full md:w-[160px]">
          <label className="block text-[13px] font-pt-sans text-black mb-[5px] tracking-wider">Full Price</label>
          <DecimalInput
            value={fullPrice || ''}
            onBlur={updateFullPrice}
            decimalPlaces={2}
            className="w-full border border-gray-40 bg-white-hard px-2.5 py-2 text-[15px] font-pt-sans"
            allowNull={true}
            min={0}
          />
        </div>

        {/* Discounted Price */}
        <div className="w-full md:w-[160px]">
          <label className="block text-[13px] font-pt-sans text-black mb-[5px] tracking-wider">Discounted Price</label>
          <DecimalInput
            value={discountedPrice || ''}
            onBlur={updateDiscountedPrice}
            decimalPlaces={2}
            className="w-full border border-gray-40 bg-white-hard px-2.5 py-2 text-[15px] font-pt-sans"
            allowNull={true}
            min={0}
          />
        </div>

        {/* Discount % */}
        <div className="w-full md:w-[160px]">
          <label className="block text-[13px] font-pt-sans text-black mb-[5px] tracking-wider">Discount %</label>
          <input
            type="number"
            value={discountPercent || ''}
            onChange={e => {
              const value = e.target.value;
              if (value === '') {
                updateDiscountPercent(null);
              } else {
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                  updateDiscountPercent(numValue);
                }
              }
            }}
            className="w-full border border-gray-40 bg-white-hard px-2.5 py-2 text-[15px] font-pt-sans"
            placeholder=""
            min="0"
            max="100"
          />
        </div>
      </div>
    </div>
  );
};
